(() => {
  const normalize = (text) => text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const decode = (text) => { const el = document.createElement('textarea'); el.innerHTML = text; return el.value; };
  let catalogPromise;
  function catalog(base) {
    if (!catalogPromise) {
      catalogPromise = (async () => {
        const page = async (number) => {
          const url = new URL(base, location.origin);
          url.searchParams.set('view', 'art-search');
          url.searchParams.set('page', number);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalog unavailable');
          const data = await response.json();
          if (!Array.isArray(data.products) || !Number.isInteger(data.pages)) throw new Error('Invalid catalog');
          return data;
        };
        const first = await page(1);
        const products = [...first.products];
        for (let i = 2; i <= first.pages; i++) products.push(...(await page(i)).products);
        return [...new Map(products.map(p => [p.id, p])).values()].map(p => ({ ...p, titleKey: normalize(p.title), bodyKey: normalize(decode(p.description)) }));
      })().catch(error => { catalogPromise = null; throw error; });
    }
    return catalogPromise;
  }
  function rank(product, query) {
    const terms = query.split(' ');
    const titleMatches = terms.filter(term => product.titleKey.includes(term)).length;
    if (!terms.every(term => product.titleKey.includes(term) || product.bodyKey.includes(term))) return 0;
    if (product.titleKey === query) return 400;
    if (product.titleKey.includes(query)) return 300;
    if (titleMatches === terms.length) return 200;
    return 100 + titleMatches / terms.length;
  }
  const make = (tag, className, text) => { const el = document.createElement(tag); el.className = className; if (text !== undefined) el.textContent = text; return el; };
  class ArtSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input[type=search]');
      this.status = this.querySelector('[role=status]');
      this.results = this.querySelector('.paper-search-results');
      this.more = this.querySelector('.paper-search-more');
      this.version = 0;
      this.querySelector('form').addEventListener('submit', event => { event.preventDefault(); clearTimeout(this.timer); this.search(true); });
      this.input.addEventListener('input', () => { this.version++; clearTimeout(this.timer); this.timer = setTimeout(() => this.search(), 180); });
      this.more.addEventListener('click', () => this.renderMore());
      if (this.hasAttribute('data-search-page') && this.input.value.trim()) this.search();
    }
    async search(submitted = false) {
      const version = ++this.version;
      const query = normalize(this.input.value);
      this.querySelector('.paper-search-toads').hidden = !/\btoads?\b/.test(query);
      if (this.hasAttribute('data-search-page')) document.title = `${this.input.value.trim() ? `Search: ${this.input.value.trim()}` : 'Search artworks'} – ${this.dataset.shopName}`;
      this.results.replaceChildren();
      this.more.hidden = true;
      if (!query) { this.status.textContent = 'Originals & prints · Available and sold'; this.removeAttribute('aria-busy'); return; }
      this.status.textContent = 'Searching artworks…';
      this.setAttribute('aria-busy', 'true');
      if (this.hasAttribute('data-search-page') && submitted) {
        const url = new URL(location.href); url.searchParams.set('q', this.input.value.trim()); history.replaceState(null, '', url);
      }
      try {
        const products = await catalog(this.dataset.catalogUrl);
        if (version !== this.version) return;
        this.rankedProducts = products.map(p => ({ product: p, rank: rank(p, query) })).filter(p => p.rank > 0).sort((a, b) => b.rank - a.rank || Number(b.product.available) - Number(a.product.available) || a.product.title.localeCompare(b.product.title) || a.product.type.localeCompare(b.product.type));
        this.shown = 0;
        this.status.textContent = this.rankedProducts.length ? `${this.rankedProducts.length} ${this.rankedProducts.length === 1 ? 'artwork' : 'artworks'} found` : 'No artworks found. Try a different title or subject.';
        this.renderMore();
      } catch (_) {
        if (version === this.version) this.status.textContent = 'Search couldn’t load. Please try again.';
      } finally { if (version === this.version) this.removeAttribute('aria-busy'); }
    }
    renderMore() {
      const batch = this.rankedProducts.slice(this.shown, this.shown + 24);
      for (const { product } of batch) {
        const card = make('article', product.available ? 'paper-card' : 'paper-card is-sold');
        const link = make('a', 'paper-card-image'); link.href = product.url;
        if (product.image) { const img = new Image(); img.src = product.image; img.alt = product.title; img.width = 600; img.height = 600; img.loading = 'lazy'; link.append(img); }
        else link.append(make('span', 'paper-image-pending', product.title));
        if (!product.available) link.append(make('span', 'paper-sold-stamp', 'Sold'));
        const heading = make('h3', ''); const title = make('a', '', product.title); title.href = product.url; heading.append(title);
        const meta = make('div', 'paper-card-meta'); meta.append(make('span', '', `${product.varies ? 'From ' : ''}${decode(product.price)}`));
        const detail = make('p', 'paper-card-detail', product.type === 'Print' ? 'Signed print' : 'Original artwork');
        card.append(link, heading, meta, detail); this.results.append(card);
      }
      this.shown += batch.length;
      this.more.hidden = this.shown >= this.rankedProducts.length;
    }
  }
  if (!customElements.get('paper-art-search')) customElements.define('paper-art-search', ArtSearch);
  const dialog = document.querySelector('#PaperSearchDialog');
  const trigger = document.querySelector('[data-art-search-open]');
  if (!dialog || !trigger || !dialog.showModal) return;
  let previousOverflow;
  trigger.addEventListener('click', event => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); previousOverflow = document.body.style.overflow; dialog.showModal(); document.body.style.overflow = 'hidden'; dialog.querySelector('input[type=search]').focus();
  });
  dialog.querySelector('[data-art-search-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } });
  dialog.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); dialog.close(); } });
  dialog.addEventListener('close', () => { document.body.style.overflow = previousOverflow; trigger.focus(); });
})();
